import { Button, Col, Flex, Grid, Image, Row } from "antd";
import React, { useState } from "react";

import { LeftOutlined, RightOutlined, StarFilled } from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router-dom";
import { H5, H6, P1, P2 } from "../../components/NormalFonts";
import { useLayout } from "../../context/LayoutContext";
import { tdkConfig } from "../../enumerations/tdkConfig";
import { useGetCoaches, useGetCoachInfo } from "../../hooks/Coach/coach.hooks";
import { CoachResponse, OrderReviewResponse } from "../../hooks/Coach/data";
import useGetTimezone from "../../hooks/Timezone/timezone.hooks";
import { ContentWrapper } from "../../layouts/components";
import { pushGtag, scrollToTop } from "../../plugins/utils";
import { BookModal, PictureContainer, TeachingMedia } from "./components";
import { CoachInfoProps } from "./data";
import { assign } from "./enumerations/assign";
import styles from "./styleModule";

const CoachInfo: React.FC<CoachInfoProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getHeaderHeight } = useLayout();
  const { toLocalTimezone } = useGetTimezone();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const pathname = window.location.pathname.split("/");
  const coachId = [...pathname].pop();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { refetch: refetchCoaches, isFetching } = useGetCoaches(
    {},
    { enabled: false }
  );
  const { data: coachInfo } = useGetCoachInfo(
    { coach_id: coachId },
    { enabled: !!coachId }
  );

  const renderReviewStars = (rating: number) => {
    const normalizedRating = Math.max(0, Math.min(5, rating ?? 0));
    return (
      <Flex align="center" gap={4}>
        {[...Array(5)].map((_, index) => (
          <StarFilled
            key={`review_star_${index}`}
            style={{
              color: index < normalizedRating ? "var(--secondary)" : "#DDDDDD",
              fontSize: screens.md ? 16 : 14,
            }}
          />
        ))}
      </Flex>
    );
  };

  const handleClickNext = () => {
    refetchCoaches().then((res) => {
      switch (res.status) {
        case "success":
          console.debug(res.data);
          const nowCoach = res.data.findIndex(
            (coach: CoachResponse) => coach.account_id === Number(coachId)
          );
          if (nowCoach === res.data.length - 1) return;
          navigate(`/trainers/info/${res.data[nowCoach + 1].account_id}`);
          scrollToTop("preview-container");
          break;
        case "error":
          console.debug(res.error);
          break;
        default:
          break;
      }
    });
  };

  const handleClickBook = () => {
    pushGtag({
      event: "book_coach",
      info: coachInfo,
    });

    setIsModalOpen(true);
  };

  const renderTDK = () => {
    const target = tdkConfig["/trainers/info"];
    return (
      <Helmet prioritizeSeoTags>
        <title>{target.title}</title>
        <meta name="description" content={target.description} />
        <meta name="keywords" content={target.keywords} />
      </Helmet>
    );
  };

  return (
    <ContentWrapper>
      {renderTDK()}
      <div
        className={`${styles.previewContainer} ${
          screens.md ? "" : styles.mobile
        }`}
        style={{ "--marginTop": getHeaderHeight() } as React.CSSProperties}
        id="preview-container"
      >
        {/* 教練介紹 */}
        <PictureContainer
          coachInfo={coachInfo}
          handleClickBook={handleClickBook}
        />

        {/* 教練簡介 */}
        <P1 color="#272727" style={{ marginBottom: screens.md ? 60 : 40 }}>
          <div
            style={{
              textWrap: "wrap",
              fontSize: screens.md ? 16 : 14,
              whiteSpace: "pre-wrap",
            }}
          >
            {coachInfo?.bio}
          </div>
        </P1>

        {/* 雪地寫真 */}
        {coachInfo?.teaching_media?.length > 0 ? (
          <TeachingMedia coachInfo={coachInfo} />
        ) : null}

        {/* 雪地影片 */}
        {coachInfo?.youtube_url ? (
          <>
            <H5
              className={styles.subTitle}
              weight="medium"
              color="var(--primary-90)"
            >
              {t("preview.youtube")}
            </H5>
            <div className={styles.playerContainer}>
              <ReactPlayer
                url={coachInfo?.youtube_url}
                playing={false}
                controls={true}
                width={"100%"}
                height={"100%"}
              />
            </div>
          </>
        ) : null}

        {/* 教練證照 */}
        {coachInfo?.certifications?.length > 0 ? (
          <>
            <H5
              className={styles.subTitle}
              weight="medium"
              color="var(--primary-90)"
            >
              {t("preview.certificates")}
            </H5>
            <Row
              gutter={[15, 12]}
              style={{ paddingBottom: screens.lg ? 100 : 44 }}
            >
              {coachInfo?.certifications?.map(
                (certificate: string, index: number) => {
                  return (
                    <Col span={screens.xs ? 12 : 6} key={`cert_${index}`}>
                      <Image src={certificate} preview={false} />
                    </Col>
                  );
                }
              )}
            </Row>
          </>
        ) : null}

        {/* 學員評價 */}
        {coachInfo?.order_review?.length > 0 && (
          <>
            <H5
              className={styles.subTitle}
              weight="medium"
              color="var(--primary-90)"
            >
              {t("coachInfo.order.review")}
            </H5>
            <Row
              gutter={[15, 16]}
              style={{
                paddingBottom: screens.lg ? 100 : 44,
                width: "100%",
                margin: "0 auto",
              }}
            >
              {coachInfo?.order_review?.map(
                (review: OrderReviewResponse, index: number) => {
                  return (
                    <Col
                      span={24}
                      key={`review_${index}`}
                      style={{ width: "100%", padding: 0 }}
                    >
                      <div className={styles.reviewItem}>
                        <div className={styles.reviewItemTitle}>
                          <H6
                            weight="normal"
                            color="var(--comment-prim)"
                            style={{
                              fontSize: screens.md ? 20 : 16,
                            }}
                          >
                            {`${t(`skiresort.${review.ski_resort_code}`)} · ${t(
                              `normal.skill.${review.skill_code}`
                            )}`}
                          </H6>
                        </div>
                        <div className={styles.reviewSubTitle}>
                          {renderReviewStars(review.rating)}
                          <P2
                            color="var(--text-content-3)"
                            style={{
                              fontSize: screens.md ? 14 : 12,
                            }}
                          >
                            {toLocalTimezone(review.created_at)?.format(
                              "YYYY-MM-DD"
                            )}
                          </P2>
                        </div>
                        <div className={styles.reviewContent}>
                          <P1
                            color="var(--text-content-1)"
                            style={{
                              fontSize: screens.md ? 16 : 14,
                            }}
                          >
                            {review.description}
                          </P1>
                        </div>
                      </div>
                    </Col>
                  );
                }
              )}
            </Row>
          </>
        )}

        {/* 操作按鈕 */}
        <Flex
          align="center"
          justify="space-between"
          className={styles.actionContainer}
        >
          <Button onClick={() => navigate("/trainers")} type="link">
            <P2 color="var(--primary-60)">
              <Flex align="center" gap={4}>
                <LeftOutlined />
                {t("coachInfo.back")}
              </Flex>
            </P2>
          </Button>
          {coachInfo?.can_assign === assign.ALLOW && (
            <Button
              className={styles.pcButton}
              size="middle"
              style={{
                background: "var(--primary)",
                color: "var(--Neutral-light-10)",
              }}
              onClick={handleClickBook}
              variant="solid"
            >
              {t("preview.book")}
            </Button>
          )}
          <Button onClick={handleClickNext} type="link" loading={isFetching}>
            <P2 color="var(--primary-60)">
              <Flex align="center" gap={4}>
                {t("coachInfo.next")}
                <RightOutlined />
              </Flex>
            </P2>
          </Button>
        </Flex>
        {coachInfo?.can_assign === assign.ALLOW && (
          <div className={styles.mobileButtonContainer}>
            <Button
              className={styles.mobileButton}
              type={"primary"}
              size="small"
              block
              style={{
                background: "var(--primary)",
                color: "var(--Neutral-light-10)",
              }}
              onClick={handleClickBook}
            >
              {t("preview.book")}
            </Button>
          </div>
        )}
      </div>
      <BookModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        coachInfo={coachInfo}
      />
    </ContentWrapper>
  );
};

export default CoachInfo;
